import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AuthLayout from '../components/AuthLayout';
import NeuralNetworkVisual from '../components/auth/NeuralNetworkVisual';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import AuthFormFooter from '../components/auth/AuthFormFooter';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { fadeInUp, staggerContainer, staggerItem } from '../utils/animations';
import { PASSWORD_RULES, MOBILE_FORMAT_HINT, validatePassword, validateMobile } from '../utils/authValidation';
import './AuthPages.css';

const INITIAL_FORM = {
  userName: '',
  email: '',
  mobileNumber: '',
  password: '',
};

function SignUp() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { toast } = useToast();
  const [form, setForm] = useState(INITIAL_FORM);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(function (prev) {
      return { ...prev, [name]: value };
    });
    setError('');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const passwordCheck = validatePassword(form.password);
    if (!passwordCheck.valid) {
      setError('Password must meet all requirements: ' + passwordCheck.failedRules.join('; '));
      setLoading(false);
      return;
    }
    if (!validateMobile(form.mobileNumber)) {
      setError('Please enter a valid mobile number (10–15 digits, with or without country code).');
      setLoading(false);
      return;
    }

    try {
      await signup(form.userName, form.email, form.mobileNumber, form.password);
      toast('Account created! Please verify your email with the code we sent.', 'success');
      // After successful signup, redirect to OTP verification page
      navigate('/otp-verification', { 
        state: { 
          userName: form.userName,
          email: form.email,
          password: form.password 
        } 
      });
    } catch (err) {
      const msg = err.message || 'Sign up failed. Please try again.';
      setError(msg);
      toast(msg, 'error');
      setLoading(false);
    }
  }

  const animationProps = isMobile ? {} : fadeInUp;
  const staggerProps = isMobile ? {} : staggerContainer;
  const itemProps = isMobile ? {} : staggerItem;

  return (
    <AuthLayout leftContent={<NeuralNetworkVisual />}>
      <motion.div {...animationProps}>
        <Card className="ui-card--glass">
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="auth-form-welcome">Join Us!</p>
            <h2 className="auth-form-title">Create Account</h2>

            <motion.div
              className="auth-form-fields"
              variants={staggerProps}
              initial={isMobile ? false : "initial"}
              animate={isMobile ? false : "animate"}
            >
              <motion.div variants={itemProps}>
                <Input
                  label="User Name"
                  name="userName"
                  type="text"
                  value={form.userName}
                  onChange={handleChange}
                  placeholder="User Name"
                  autoComplete="user-name"
                  required
                  glass
                />
              </motion.div>
              {/* <motion.div variants={staggerItem}>
                <Input
                  label="Last Name"
                  name="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Last Name"
                  autoComplete="family-name"
                  required
                  glass
                />
              </motion.div> */}
              <motion.div variants={itemProps}>
                <Input
                  label="Email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="Email"
                  autoComplete="email"
                  required
                  glass
                />
              </motion.div>
              <motion.div variants={itemProps}>
                <Input
                  label="Mobile Number"
                  name="mobileNumber"
                  type="tel"
                  value={form.mobileNumber}
                  onChange={handleChange}
                  placeholder="e.g. +1234567890"
                  autoComplete="tel"
                  required
                  glass
                />
                <p className="auth-field-hint">{MOBILE_FORMAT_HINT}</p>
              </motion.div>
              <motion.div variants={itemProps}>
                <Input
                  label="Password"
                  name="password"
                  type="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Password"
                  autoComplete="new-password"
                  required
                  glass
                />
                <p className="auth-field-hint auth-password-rules-label">Password must:</p>
                <ul className="auth-password-rules">
                  {PASSWORD_RULES.map((rule, i) => (
                    <li key={i}>{rule}</li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>

            {error && (
              <motion.div
                initial={isMobile ? {} : { opacity: 0, y: -10 }}
                animate={isMobile ? {} : { opacity: 1, y: 0 }}
                className="auth-form-error"
                style={{ color: 'var(--error-color, #ff4444)', marginBottom: '1rem', fontSize: '0.875rem' }}
              >
                {error}
              </motion.div>
            )}

            <motion.div
              whileHover={isMobile ? {} : { scale: 1.02 }}
              whileTap={isMobile ? {} : { scale: 0.98 }}
            >
              <Button
                type="submit"
                variant="primary"
                fullWidth
                className="auth-form-submit"
                disabled={loading}
              >
                {loading ? 'Signing Up...' : 'Sign Up'}
              </Button>
            </motion.div>

            <AuthFormFooter
              text="Already have an account?"
              linkTo="/signin"
              linkLabel="Sign In"
            />
          </form>
        </Card>
      </motion.div>
    </AuthLayout>
  );
}

export default SignUp;
